// The booking / show-spine hooks — hand-written over the generated client via the
// tenant-keyed wrappers. Keys are ALWAYS ['band', bandId, …] with bandId from the mounted
// BandContext, so a module cannot fetch outside its tenant. Every shape here is MONEY-FREE
// financials/settlement are separate money-gated sub-resources. The `schedule-changed` signal on
// `band:{bandId}` invalidates the ['band', bandId, 'shows'] + ['band', bandId, 'show'] keys (invalidationRegistry).
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext, useBandContextOrNull } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type TicketTiersRead = components["schemas"]["TicketTiersRead"];
type TicketTierInput = components["schemas"]["TicketTierInput"];
type ShowSummaryPaged = components["schemas"]["ShowSummaryPaged"];
type ShowRead = components["schemas"]["ShowRead"];
type ShowDetail = components["schemas"]["ShowDetail"];
type CreateShowRequest = components["schemas"]["CreateShowRequest"];
type UpdateShowRequest = components["schemas"]["UpdateShowRequest"];
type LineupSlotDto = components["schemas"]["LineupSlotDto"];
export type BandActSearchList = components["schemas"]["BandActSearchList"];
export type BandActSearchResult = components["schemas"]["BandActSearchResult"];
type TourSummaryPaged = components["schemas"]["TourSummaryPaged"];
type TourDetail = components["schemas"]["TourDetail"];
type CreateTourRequest = components["schemas"]["CreateTourRequest"];
type MemberList = components["schemas"]["MemberList"];
type MemberDto = components["schemas"]["MemberDto"];
type ShowInviteList = components["schemas"]["ShowInviteList"];
export type ShowInviteDto = components["schemas"]["ShowInviteDto"];
type MemberRefDto = components["schemas"]["MemberRefDto"];

/** An off/travel calendar day — the tour-days surface the OffDay modal writes. Money-free.*/
export type TourDayDetail = components["schemas"]["TourDayDetail"];
export type CreateTourDayRequest = components["schemas"]["CreateTourDayRequest"];
export type UpdateTourDayRequest = components["schemas"]["UpdateTourDayRequest"];

export interface ShowFilters {
  /** `hold` | `offer` | `confirmed` | `settled` | `cancelled`. */
  status?: string;
  tourId?: string;
  /** `date` (ascending — upcoming first) or `-date` (descending, default). */
  sort?: string;
  from?: string;
  to?: string;
  /** Page size (server default 25, max 100) — a surface listing a whole run asks for more. */
  limit?: number;
}

/** GET the band's shows (money-free list). Filters ride the query key so each filtered view caches
 * independently while `['band', bandId, 'shows']` (partial match) invalidates them all on `schedule-changed`. */
export function useShows(filters: ShowFilters = {}) {
  const { api } = useApp();
  return useBandQuery<ShowSummaryPaged>(
    ["shows", filters],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows", {
        params: {
          path: { bandId },
          query: {
            status: filters.status,
            tourId: filters.tourId,
            sort: filters.sort,
            from: filters.from,
            to: filters.to,
            limit: filters.limit,
          },
        },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 },
  );
}

/** GET the tours the shows list groups under.*/
export function useTours() {
  const { api } = useApp();
  return useBandQuery<TourSummaryPaged>(
    ["tours"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/tours", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/** GET one show — the `{ data, version }` preview + Basics/Lineup source. `version` is the e_ver the
 * edit PUT sends back as If-Match.*/
export function useShow(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<ShowRead>(
    ["show", showId ?? "new"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/{showId}", {
        params: { path: { bandId, showId: showId! } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: !!showId, staleTime: 15_000 },
  );
}

/** POST a new show/hold. Not optimistic — a create is a confirmation-state write.*/
export function useCreateShow() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<ShowDetail, CreateShowRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/shows", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (created) => {
        // Seed the just-created show into the detail key the editor reads. The Booking editor does NOT
        // unmount when create swaps `/booking/shows/new` → `/booking/shows/{id}/edit` (both URLs render one
        // component), so without this the open dialog would drop to its loading state and flash "Untitled
        // show" while a GET it already has the answer to comes back.
        // `version: 0` is the created row's e_ver, not a guess: e_ver DEFAULTs to 0 and the increment trigger
        // is BEFORE UPDATE, so it does not fire on INSERT (ShowsService.CreateShowAsync says so in place).
        // That keeps the very next Basics save sending a correct `If-Match: "0"`.
        if (created.showId) {
          qc.setQueryData(["band", bandId, "show", created.showId], { data: created, version: 0 });
        }
        void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't create show")),
    },
  );
}

/** PUT edit a show's non-money sections. Sends `If-Match: "<version>"` — a stale version
 * is 409 `version-conflict`, surfaced verbatim so the caller re-fetches. Returns the fresh `{ data, version }`. */
export function useUpdateShow() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<ShowRead, { showId: string; version: number; body: UpdateShowRequest }>(
    async (band, { showId, version, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/shows/{showId}", {
        params: { path: { bandId: band, showId } },
        headers: { "If-Match": `"${version}"` },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save changes")),
    },
  );
}

/** DELETE (soft) a show.*/
export function useDeleteShow() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, string>(
    async (band, showId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/shows/{showId}", {
        params: { path: { bandId: band, showId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      // A deleted show also leaves the holds list and the Opportunities show-invite feed (ART-84).
      onSuccess: () => {
        for (const key of ["shows", "holds", "show-invites"]) {
          void qc.invalidateQueries({ queryKey: ["band", bandId, key] });
        }
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't cancel the show")),
    },
  );
}

/** PUT replace a show's lineup (editor section 02). Full replace; returns the new ordered slots.*/
export function useReplaceLineup() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<LineupSlotDto[], { showId: string; slots: LineupSlotDto[] }>(
    async (band, { showId, slots }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/shows/{showId}/lineup", {
        params: { path: { bandId: band, showId } },
        body: { slots },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save lineup")),
    },
  );
}

/** Below this length the registered-band search never runs (server mirrors the same 2-char floor). */
export const MIN_ACT_SEARCH_LENGTH = 2;

/**
 * GET registered CNTRL bands matching `q`, for the lineup picker (editor section 02). A pick stamps the
 * result's `bandId` onto the slot's `friendBandId`, which is what lights the AC mark on the bill line — so
 * ANY registered band added to a bill is marked, not only friends (ART-165; friendship still gates SHARING
 * the show, never the mark). Enabled only at length ≥ 2; the server rate-limits and degrades to an empty
 * list on a trip, so an empty result is a normal state, never an error.
 */
export function useActBandSearch(q: string) {
  const { api } = useApp();
  const query = q.trim();
  return useBandQuery<BandActSearchList>(
    ["shows", "act-search", query],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/act-search", {
        params: { path: { bandId }, query: { q: query } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: query.length >= MIN_ACT_SEARCH_LENGTH, staleTime: 60_000 },
  );
}

/** POST a new tour — name + off/travel days.*/
export function useCreateTour() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TourDetail, CreateTourRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tours", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "tours"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't create tour")),
    },
  );
}

// ---- Tour days (off / travel days) — the OffDay modal surface --------------------
// MONEY-FREE by construction (an off/travel day carries no money — kind ∈ {off, travel}, city XOR route,
// last-write no e_ver). Writes invalidate the ops-calendar (the read surface that renders off/travel markers)
// AND the tour-days key. Confirmation-state writes (not optimistic); the server error surfaces verbatim (a
// NUL char / bad kind / partial route / a contact outside the rolodex all 400 with a field message).

type TourDayListRead = components["schemas"]["TourDayListRead"];

/**
 * GET the band's off/travel days in a date window — the READ side of the surface `OffDayModal` has been
 * writing since it shipped. Without a reader an off day was saved and then invisible everywhere except the
 * ops calendar, which reads as "it vanished"; the Booking timeline interleaves these rows by date.
 *
 * Money-free. The key is `['band', bandId, 'tour-days', …]`, which the tour-day write hooks below already
 * invalidate, so a create/edit/delete refreshes the list without a new signal.
 */
export function useTourDays(from?: string, to?: string) {
  const { api } = useApp();
  return useBandQuery<TourDayListRead>(
    ["tour-days", from ?? "", to ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/tour-days", {
        params: { path: { bandId }, query: { from, to } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/** ART-240 runs — the show/day grouping (name derived from its date range). Money-free (schedule tier). */
export type RunListRead = components["schemas"]["RunListRead"];
export type RunLinkOption = components["schemas"]["RunLinkOption"];

export function useRuns() {
  const { api } = useApp();
  return useBandQuery<RunListRead>(
    ["runs"],
    async (band) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/runs", {
        params: { path: { bandId: band } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 },
  );
}

/** POST a new (empty) run — the show editor's "+ New run" links a show to the returned id. */
export function useCreateRun() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<RunLinkOption, void>(
    async (band) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/runs", {
        params: { path: { bandId: band } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "runs"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "tour-days", "link-options"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't create the run")),
    },
  );
}

/** ART-240 link picker — confirmed shows within 3 days of `date` + the band's runs. Money-free (schedule
 * tier). Keyed by date so a date change refetches. */
export type TourDayLinkOptions = components["schemas"]["TourDayLinkOptions"];

export function useTourDayLinkOptions(date: string | undefined) {
  const { api } = useApp();
  return useBandQuery<TourDayLinkOptions>(
    ["tour-days", "link-options", date ?? ""],
    async (band) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/tour-days/link-options", {
        params: { path: { bandId: band }, query: { date: date! } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: !!date, staleTime: 15_000 },
  );
}

/** POST a standalone or tour-bound off/travel day. Returns the created `TourDayDetail`.*/
export function useCreateTourDay() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TourDayDetail, CreateTourDayRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tour-days", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "tour-days"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save the day")),
    },
  );
}

/** PUT edit an off/travel day — full-replace of the editable fields (no `tourId`; last-write, no
 * e_ver, so no If-Match). Returns the fresh `TourDayDetail`. */
export function useUpdateTourDay() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<TourDayDetail, { tourDayId: string; body: UpdateTourDayRequest }>(
    async (band, { tourDayId, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/tour-days/{tourDayId}", {
        params: { path: { bandId: band, tourDayId } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "tour-days"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save the day")),
    },
  );
}

/** DELETE (soft) an off/travel day — cascades its member scoping.*/
export function useDeleteTourDay() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, string>(
    async (band, tourDayId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/tour-days/{tourDayId}", {
        params: { path: { bandId: band, tourDayId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "tour-days"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't delete the day")),
    },
  );
}

// ---- Members (editor section 03) + holds funnel ------------------------------
// All MONEY-FREE. The members list key is ['band', bandId, 'show', showId, 'members'] — a PREFIX
// CHILD of ['band', bandId, 'show'], so the existing `schedule-changed` handler (which the RSVP/invite writes
// emit on ac.ui.band.{bandId}) already invalidates it: RSVP "Live" rides `schedule-changed`, no new signal.

/** GET the show's members + RSVP state. 403/404 surface as the query's error state.*/
/**
 * Editor section 01's ticket tiers — `GET/PUT …/shows/{showId}/ticket-tiers`. `schedule:*`, not the money
 * gate: a planned FACE price is contract data the whole schedule tier authors (the section-10 precedent).
 */
export function useTicketTiers(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<TicketTiersRead>(
    ["show", showId ?? "new", "ticket-tiers"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/ticket-tiers",
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: !!showId, staleTime: 15_000 },
  );
}

/** PUT the tiers (full replace). Σ caps syncs the show's capacity server-side, so the show read is
 *  invalidated too — the Capacity field on the same form has to repaint. */
export function useReplaceTicketTiers() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<TicketTiersRead, { showId: string; items: TicketTierInput[] }>(
    async (band, { showId, items }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/ticket-tiers",
        { params: { path: { bandId: band, showId } }, body: { items } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, { showId }) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId, "ticket-tiers"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId] });
      },
    },
  );
}

export function useShowMembers(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<MemberList>(
    ["show", showId, "members"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/members",
        // `enabled: !!showId` gates the query, so the id is present by the time this runs.
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled: !!showId },
  );
}

/** PUT replace the show's member set. Full replace; returns the fresh list.*/
export function useSetMembers() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<MemberList, { showId: string; members: MemberRefDto[] }>(
    async (band, { showId, members }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/members",
        { params: { path: { bandId: band, showId } }, body: { members } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "members"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId] });
        invalidatePerDiemReaders(qc, bandId);
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't update members")),
    },
  );
}

/** POST additional invitees — additive; emits `show_opportunity` notifications server-side.*/
export function useInviteMembers() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<MemberList, { showId: string; members: MemberRefDto[] }>(
    async (band, { showId, members }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/members/invite",
        { params: { path: { bandId: band, showId } }, body: { members } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "members"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId] });
        invalidatePerDiemReaders(qc, bandId);
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't invite members")),
    },
  );
}

/**
 * POST an RSVP response (`{ response: 'in' | 'out' }`). NOT optimistic — like `useScanGuest` this is
 * server-confirm THEN repaint (RSVP flips shared vote state), so we invalidate on success and surface the
 * server error verbatim; every other authorized client repaints from the `schedule-changed` live signal.
 */
export function useRespondMember(showId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<MemberDto, { memberId: string; response: "in" | "out" }>(
    async (band, { memberId, response: rsvp }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/members/{memberId}/respond",
        { params: { path: { bandId: band, showId, memberId } }, body: { response: rsvp } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        // The whole show prefix, not just members: a Pass changes the per-diem head-count (ART-296).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId] });
        invalidatePerDiemReaders(qc, bandId);
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save your response")),
    },
  );
}

/**
 * GET the caller's OWN show/hold invites — the Opportunities feed's second source. Scoped server-side by
 * the join to the caller's member row, so this returns the dates you were actually put on, not the band's
 * funnel (that is `useHolds`, and it is a schedule:write surface). Money-free.
 */
export function useShowInvites() {
  const { api } = useApp();
  return useBandQuery<ShowInviteList>(
    ["show-invites"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/invites", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/**
 * The feed's RSVP: the same `POST …/members/{memberId}/respond` {@link useRespondMember} uses, but taking
 * the showId PER CALL — a feed row carries its own show, so one hook serves every row. Server-confirm then
 * repaint (RSVP flips shared vote state), invalidating the invite feed alongside the show keys.
 */
export function useRespondShowInvite() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<MemberDto, { showId: string; memberId: string; response: "in" | "out" }>(
    async (band, { showId, memberId, response: rsvp }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/members/{memberId}/respond",
        { params: { path: { bandId: band, showId, memberId } }, body: { response: rsvp } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show-invites"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId] });
        invalidatePerDiemReaders(qc, bandId);
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save your response")),
    },
  );
}

/**
 * Refetch every list the Booking page draws.
 *
 * The editor's Save fans out over nine independent resources, and only SOME of them invalidate on their own
 * (`useConfirmHold` does; a plain section PUT invalidates its own show record but not the lists it appears
 * in). So a save could change a show's date, venue or status and leave the list behind the modal showing the
 * old row — the modal closes onto stale content, which reads as "the save didn't work" (Ryan, 2026-08-23).
 *
 * Deliberately broad: `shows` (its `money` sibling nests under the same prefix, so both go), `tours`,
 * `tour-days` and `holds`. A save is rare and user-initiated, so a few extra refetches cost nothing next to
 * a list that disagrees with what was just written.
 */
export function useRefreshBookingLists(): () => void {
  // The NULL-SAFE context read on purpose: this hook is called from the route component, which sits OUTSIDE
  // its own <RequireBand> gate. The throwing `useBandContext()` would crash there for a person with no band
  // seat — the exact case RequireBand exists to render a designed state for. With no band there is no list
  // to refresh, so the callback is simply a no-op.
  const band = useBandContextOrNull();
  const bandId = band?.bandId;
  const qc = useQueryClient();
  return useCallback(() => {
    if (!bandId) return;
    for (const key of ["shows", "tours", "tour-days", "holds"]) {
      void qc.invalidateQueries({ queryKey: ["band", bandId, key] });
    }
  }, [qc, bandId]);
}

/**
 * POST promote a hold to a confirmed show — returns the fresh `{ data, version }`. A 409
 * `hold-not-promotable` surfaces verbatim via ApiError (the server's ProblemDetails.detail becomes the toast).
 */
export function useConfirmHold() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<ShowRead, string>(
    async (band, showId) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/confirm",
        { params: { path: { bandId: band, showId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, showId) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "holds"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't confirm hold")),
    },
  );
}

/**
 * PUT a show's ANNOUNCED flag — the manual "this date is public" signal (it replaced the Bandsintown publish).
 * The booking boards drop announced shows from their confirmed/deal columns, so their keys refresh too.
 */
export function useSetShowAnnounced() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<ShowRead, { showId: string; announced: boolean }>(
    async (band, { showId, announced }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/announced",
        { params: { path: { bandId: band, showId } }, body: { announced } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, { showId }) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "booking"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't update the announced state")),
    },
  );
}

/** GET the dedicated holds funnel — money-free hold summaries, its own invalidatable key.*/
export function useHolds() {
  const { api } = useApp();
  return useBandQuery<ShowSummaryPaged>(
    ["holds"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/holds", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 },
  );
}

// ---- editor sections 05 Run of Show / 06 Setlist / 07 VIP / 10 Custom Expenses + Needs-Media --------
// All MONEY-FREE except that Custom Expenses carries a planned amount (schedule-tier, NOT the money
// gate). Every sub-resource key is a PREFIX CHILD of ['band', bandId, 'show', showId], so the
// existing `schedule-changed` handler already invalidates them — no new UI signal.

type RunOfShowRead = components["schemas"]["RunOfShowRead"];
type RunOfShowParseResult = components["schemas"]["RunOfShowParseResult"];
type ParseRunOfShowRequest = components["schemas"]["ParseRunOfShowRequest"];
type UpdateRunOfShowRequest = components["schemas"]["UpdateRunOfShowRequest"];
type VipRead = components["schemas"]["VipRead"];
type UpdateVipRequest = components["schemas"]["UpdateVipRequest"];
type SetlistRead = components["schemas"]["SetlistRead"];
type ReplaceSetlistRequest = components["schemas"]["ReplaceSetlistRequest"];
type SongBankRead = components["schemas"]["SongBankRead"];
type ExpensesRead = components["schemas"]["ExpensesRead"];
type ReplaceExpensesRequest = components["schemas"]["ReplaceExpensesRequest"];

/** Invalidate a show's record + its sub-resources + the shows list after a section write. */
/** The show's member list is its per-diem head-count (ART-296), so a member change moves money read OUTSIDE
 *  the show's own key prefix: the booking money list (Show Total) and Outstanding Payments' nets. */
function invalidatePerDiemReaders(qc: ReturnType<typeof useQueryClient>, bandId: string) {
  void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
  void qc.invalidateQueries({ queryKey: ["band", bandId, "outstanding"] });
}

function invalidateShow(qc: ReturnType<typeof useQueryClient>, bandId: string, showId: string) {
  void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId] });
  void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] });
}

/** GET the show's run-of-show rows.*/
export function useRunOfShow(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<RunOfShowRead>(
    ["show", showId, "run-of-show"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/run-of-show",
        // `enabled: !!showId` gates the query, so the id is present by the time this runs.
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled: !!showId },
  );
}

/** PUT replace the run-of-show rows — fires `logistics_added` server-side.*/
/**
 * POST run-of-show/parse — a day-sheet screenshot → per-act rows already ATTRIBUTED to the bill, plus the
 * show-wide block, in exactly the shapes the PUT takes. Persists NOTHING: the user confirms first.
 *
 * Does not toast. A 502 (Gemini out) is a degrade the section explains inline, because the fallback — type
 * it in — is the thing the user was already about to do.
 */
export function useParseRunOfShow() {
  const { api } = useApp();
  return useBandMutation<RunOfShowParseResult, { showId: string; body: ParseRunOfShowRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/run-of-show/parse",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  );
}

export function useUpdateRunOfShow() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<RunOfShowRead, { showId: string; body: UpdateRunOfShowRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/run-of-show",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "run-of-show"] });
        invalidateShow(qc, bandId, vars.showId);
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save run of show")),
    },
  );
}

/** GET the show's VIP block.*/
export function useVip(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<VipRead>(
    ["show", showId, "vip"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/vip",
        // `enabled: !!showId` gates the query, so the id is present by the time this runs.
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled: !!showId },
  );
}

/** PUT replace the VIP block.*/
export function useUpdateVip() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<VipRead, { showId: string; body: UpdateVipRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/vip",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "vip"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save VIP")),
    },
  );
}

/** GET the show's setlist.*/
export function useSetlist(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<SetlistRead>(
    ["show", showId, "setlist"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/setlist",
        // `enabled: !!showId` gates the query, so the id is present by the time this runs.
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled: !!showId },
  );
}

/** PUT replace the setlist — full replace (copy-from-last + add-your-own).*/
export function useReplaceSetlist() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SetlistRead, { showId: string; body: ReplaceSetlistRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/setlist",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "setlist"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "song-bank"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save setlist")),
    },
  );
}

/** POST copy the setlist from the band's most-recent prior show. Returns the seeded setlist.*/
export function useCopySetlistFromLast() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SetlistRead, string>(
    async (band, showId) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/setlist/copy-from-last",
        { params: { path: { bandId: band, showId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, showId) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId, "setlist"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't copy setlist")),
    },
  );
}

/** GET the Song Bank picker — custom songs live, catalog degrades until Phase 4.*/
export function useSongBank() {
  const { api } = useApp();
  return useBandQuery<SongBankRead>(
    ["song-bank"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/song-bank", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/** GET the show's planned custom-expense lines.*/
export function useExpenses(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<ExpensesRead>(
    ["show", showId, "expenses"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/expenses",
        // `enabled: !!showId` gates the query, so the id is present by the time this runs.
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled: !!showId },
  );
}

/** PUT replace the planned custom-expense lines.*/
export function useUpdateExpenses() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ExpensesRead, { showId: string; body: ReplaceExpensesRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/expenses",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "expenses"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save expenses")),
    },
  );
}

// ---- ART-305 v2: Add expense from the Calendar ----------------------------------------------------------------

type AddMyExpenseRequest = components["schemas"]["AddMyExpenseRequest"];

/** The expenses the VIEWER added to this show from the Calendar ("anyone can … view the expenses they've added",
 *  PO). Disabled while `showId` is unset. */
export function useMyShowExpenses(showId: string | undefined, enabled = true) {
  const { api } = useApp();
  return useBandQuery<ExpensesRead>(
    ["show", showId, "expenses", "mine"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/{showId}/expenses/mine", {
        params: { path: { bandId, showId: showId! } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled: enabled && !!showId },
  );
}

/** Add an expense the viewer paid. It lands in the show's Expenses on the Booking page and, as reimbursable, in
 *  Outstanding Payments — so every read of those repaints. */
export function useAddMyExpense() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ExpensesRead, { showId: string; body: AddMyExpenseRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/shows/{showId}/expenses/mine", {
        params: { path: { bandId: band, showId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId] });
        // the show (its expenses, settlement, breakdown), the Booking list's money, Outstanding Payments
        for (const key of ["shows", "outstanding", "ops-calendar"]) {
          void qc.invalidateQueries({ queryKey: ["band", bandId, key] });
        }
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't add the expense")),
    },
  );
}

// ---- Quotes (guarantee quote builder) + tour RSVP — SCHEDULE-tier, money-free ---------------
// A saved quote is a booking-planning worksheet authored under schedule:read/write, NOT the money
// gate — so these hooks live here in the money-free file (the `computedGuaranteeCents` is a floor-price output,
// like the travel-cost / custom-expense surfaces). The tour RSVP is the one-RSVP-covers-all-dates action.

type QuoteListRead = components["schemas"]["QuoteListRead"];
type QuoteDetail = components["schemas"]["QuoteDetail"];
type SaveQuoteRequest = components["schemas"]["SaveQuoteRequest"];
type TourRsvpResult = components["schemas"]["TourRsvpResult"];
type RespondTourRequest = components["schemas"]["RespondTourRequest"];

/** GET the band's saved quotes. Money-free (schedule:read).*/
export function useQuotes() {
  const { api } = useApp();
  return useBandQuery<QuoteListRead>(
    ["quotes"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/quotes", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/** POST/PUT a quote worksheet. An absent `quoteId` creates; a present one overwrites (single-editor).*/
export function useSaveQuote() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<QuoteDetail, { quoteId?: string; body: SaveQuoteRequest }>(
    async (band, { quoteId, body }) => {
      if (quoteId) {
        const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/quotes/{quoteId}", {
          params: { path: { bandId: band, quoteId } },
          body,
        });
        if (!response.ok || !data?.data) throw new ApiError(response.status, error);
        return data.data;
      }
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/quotes", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "quotes"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save quote")),
    },
  );
}

/** DELETE (soft) a saved quote.*/
export function useDeleteQuote() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, string>(
    async (band, quoteId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/quotes/{quoteId}", {
        params: { path: { bandId: band, quoteId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "quotes"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't delete quote")),
    },
  );
}

/** POST a tour-wide RSVP — one call covers every date under the tour.*/
export function useRespondTour() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<TourRsvpResult, { tourId: string; body: RespondTourRequest }>(
    async (band, { tourId, body }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tours/{tourId}/respond", {
        params: { path: { bandId: band, tourId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "shows"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't respond to tour")),
    },
  );
}

// ---- Ops calendar (money-free-by-construction variant) — schedule:read -----------------------------
// The read-only ops calendar for the Production/Content/Merch/crew/Label tier — it carries NO money shape by
// construction. Same schedule domain, a different (money-free) endpoint.

type OpsCalendarView = components["schemas"]["OpsCalendarView"];

/**
 * GET the money-free ops calendar. `from`/`to` are ISO dates; the server clamps an over-wide window.
 *
 * **There is no `scope` argument, and its removal is the ruling (PO, 2026-09-11).** Whose calendar this is
 * falls out of the caller's own seat, server-side: a band seat gets the dates it is named on, a label gets
 * the booked shows, the content days and whatever a colleague was invited on. The old Whole-band / My-dates
 * toggle let a crew member widen their own calendar to the band's whole routing with one click — and, being
 * a client control over a server narrow, it was a second authority on a question only the server can answer.
 */
export function useOpsCalendar(from?: string, to?: string) {
  const { api } = useApp();
  return useBandQuery<OpsCalendarView>(
    ["ops-calendar", from ?? "", to ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/schedule/ops-calendar", {
        params: { path: { bandId }, query: { from, to } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

// ---- ART-305: the Calendar's event details --------------------------------------------------------------------

type OpsCalendarItemDetail = components["schemas"]["OpsCalendarItemDetail"];
type OpsCalendarMyPay = components["schemas"]["OpsCalendarMyPay"];
/** Which table an ops-calendar row's details live in. */
export type OpsCalendarItemKind = "show" | "tour_day" | "opportunity";

/** One event's read-only details (`GET …/ops-calendar/items/{kind}/{id}`). Money-free — the viewer's own pay is
 *  {@link useOpsCalendarMyPay}. Disabled while nothing is open. */
export function useOpsCalendarItem(kind: OpsCalendarItemKind | null, id: string | null) {
  const { api } = useApp();
  return useBandQuery<OpsCalendarItemDetail>(
    ["ops-calendar", "item", kind ?? "", id ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/schedule/ops-calendar/items/{itemKind}/{itemId}", {
        params: { path: { bandId, itemKind: kind ?? "", itemId: id ?? "" } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: !!kind && !!id, staleTime: 15_000 },
  );
}

/** The VIEWER's own pay on one event — its own money-gated read, asked only when `enabled` (a seat holding show
 *  money at some grain, on an event they are on). A refusal or a failure simply means no pay line. */
export function useOpsCalendarMyPay(kind: OpsCalendarItemKind | null, id: string | null, enabled: boolean) {
  const { api } = useApp();
  return useBandQuery<OpsCalendarMyPay>(
    ["ops-calendar", "item", kind ?? "", id ?? "", "my-pay"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/schedule/ops-calendar/items/{itemKind}/{itemId}/my-pay", {
        params: { path: { bandId, itemKind: kind ?? "", itemId: id ?? "" } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!kind && !!id, staleTime: 15_000, retry: false },
  );
}

/** Accept (`in`) or back out of / decline (`out`) a show or opportunity from its calendar details. Server-confirm
 *  then repaint: the calendar, the open details and every list the answer moves. */
export function useRespondCalendarItem() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { kind: OpsCalendarItemKind; id: string; response: "in" | "out" }>(
    async (band, { kind, id, response: answer }) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/schedule/ops-calendar/items/{itemKind}/{itemId}/respond", {
        params: { path: { bandId: band, itemKind: kind, itemId: id } },
        body: { response: answer },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => {
        for (const key of ["ops-calendar", "show-invites", "opportunities", "shows"]) {
          void qc.invalidateQueries({ queryKey: ["band", bandId, key] });
        }
        // The roster artist page's Opportunities card reads its composed dashboard, so an answer given from
        // these Details has to refetch that too or the row stays (ART-338).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save your answer")),
    },
  );
}

/** Remove an off/travel day from the VIEWER's calendar only — the tour itinerary is unchanged for everyone else. */
export function useHideCalendarDay() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { id: string }>(
    async (band, { id }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/schedule/ops-calendar/items/tour_day/{tourDayId}/hidden", {
        params: { path: { bandId: band, tourDayId: id } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't remove it from your calendar")),
    },
  );
}
